import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Shield,
  AlertTriangle,
  CheckCircle,
  UserCheck,
  FileText,
  Edit,
  Save,
  X,
} from 'lucide-react';

interface DEALogDetail {
  log_id: string;
  transaction_date: string;
  transaction_type: string;
  drug_name: string;
  drug_strength: string;
  drug_form: string;
  ndc_code: string | null;
  dea_schedule: string;
  lot_number: string | null;
  expiration_date: string | null;
  quantity_received: number | null;
  quantity_administered: number | null;
  quantity_wasted: number | null;
  quantity_remaining: number;
  unit_of_measure: string;
  running_balance: number;
  patient_id: string | null;
  patient_name: string | null;
  procedure_name: string | null;
  provider_id: string | null;
  provider_name: string;
  provider_dea_number: string | null;
  witness_id: string | null;
  witness_name: string | null;
  witness_signature_timestamp: string | null;
  witness_verified: boolean;
  supplier_name: string | null;
  invoice_number: string | null;
  form_222_number: string | null;
  discrepancy_flag: boolean;
  discrepancy_type: string | null;
  discrepancy_amount: number | null;
  discrepancy_notes: string | null;
  discrepancy_resolved: boolean;
  discrepancy_resolved_at: string | null;
  notes: string | null;
  created_at: string;
  created_by_name: string;
  modified_at: string | null;
}

export function DEALogDetailRoute() {
  const { logId } = useParams<{ logId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState(false);
  const [editNotes, setEditNotes] = useState('');

  // Witness verification modal
  const [witnessModalOpen, setWitnessModalOpen] = useState(false);
  const [witnessId, setWitnessId] = useState('');
  const [witnessName, setWitnessName] = useState('');

  // Discrepancy modal
  const [discrepancyModalOpen, setDiscrepancyModalOpen] = useState(false);
  const [discrepancyType, setDiscrepancyType] = useState('');
  const [discrepancyAmount, setDiscrepancyAmount] = useState('');
  const [discrepancyNotes, setDiscrepancyNotes] = useState('');

  // Resolution modal
  const [resolutionModalOpen, setResolutionModalOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');

  // Fetch log entry details
  const { data: logEntry, isLoading } = useQuery({
    queryKey: ['dea-log-detail', logId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dea_controlled_substance_log')
        .select('*')
        .eq('log_id', logId)
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      return data as DEALogDetail;
    },
    enabled: !!logId,
  });

  // Fetch available providers for witness selection
  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('provider_id, first_name, last_name')
        .order('last_name');

      if (error) throw error;
      return data;
    },
  });

  // Verify witness mutation
  const verifyWitnessMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('verify_dea_witness', {
        p_log_id: logId,
        p_witness_id: witnessId,
        p_witness_name: witnessName,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dea-log-detail', logId] });
      queryClient.invalidateQueries({ queryKey: ['dea-log'] });
      queryClient.invalidateQueries({ queryKey: ['dea-stats'] });
      setWitnessModalOpen(false);
      setWitnessId('');
      setWitnessName('');
    },
  });

  // Flag discrepancy mutation
  const flagDiscrepancyMutation = useMutation({
    mutationFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('flag_dea_discrepancy', {
        p_log_id: logId,
        p_discrepancy_type: discrepancyType,
        p_discrepancy_amount: parseFloat(discrepancyAmount),
        p_discrepancy_notes: discrepancyNotes,
        p_flagged_by_id: user.id,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dea-log-detail', logId] });
      queryClient.invalidateQueries({ queryKey: ['dea-log'] });
      queryClient.invalidateQueries({ queryKey: ['dea-stats'] });
      setDiscrepancyModalOpen(false);
      setDiscrepancyType('');
      setDiscrepancyAmount('');
      setDiscrepancyNotes('');
    },
  });

  // Resolve discrepancy mutation
  const resolveDiscrepancyMutation = useMutation({
    mutationFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('resolve_dea_discrepancy', {
        p_log_id: logId,
        p_resolved_by_id: user.id,
        p_resolution_notes: resolutionNotes,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dea-log-detail', logId] });
      queryClient.invalidateQueries({ queryKey: ['dea-log'] });
      queryClient.invalidateQueries({ queryKey: ['dea-stats'] });
      setResolutionModalOpen(false);
      setResolutionNotes('');
    },
  });

  // Update notes mutation
  const updateNotesMutation = useMutation({
    mutationFn: async (notes: string) => {
      const { error } = await supabase
        .from('dea_controlled_substance_log')
        .update({ notes, modified_at: new Date().toISOString() })
        .eq('log_id', logId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dea-log-detail', logId] });
      setEditMode(false);
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-full bg-navy-950 text-pt-text flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gold-400 mx-auto mb-4 animate-pulse" />
          <p className="text-pt-text-muted">Loading DEA log entry...</p>
        </div>
      </div>
    );
  }

  if (!logEntry) {
    return (
      <div className="min-h-full bg-navy-950 text-pt-text flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-pt-red mx-auto mb-4" />
          <p className="text-pt-text-muted">DEA log entry not found</p>
        </div>
      </div>
    );
  }

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      receipt: 'Receipt',
      administration: 'Administration',
      waste: 'Waste',
      transfer_in: 'Transfer In',
      transfer_out: 'Transfer Out',
      return: 'Return',
      destruction: 'Destruction',
      theft_loss: 'Theft/Loss',
      inventory_adjustment: 'Adjustment',
      emergency_use: 'Emergency Use',
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-full bg-navy-950 text-pt-text p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dea-log')}
          className="flex items-center gap-2 text-pt-cyan hover:text-pt-cyan mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to DEA Log
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-gold-400" />
            <div>
              <h1 className="text-3xl font-bold font-rajdhani">DEA Transaction Detail</h1>
              <p className="text-pt-text-muted text-sm">
                {format(new Date(logEntry.transaction_date), 'MMMM dd, yyyy h:mm a')}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {logEntry.transaction_type === 'waste' && !logEntry.witness_verified && (
              <button
                onClick={() => setWitnessModalOpen(true)}
                className="flex items-center gap-2 bg-pt-amber/20 text-pt-amber px-4 py-2 rounded border border-pt-amber/30 hover:bg-pt-amber/30"
              >
                <UserCheck className="w-4 h-4" />
                Verify Witness
              </button>
            )}

            {!logEntry.discrepancy_flag && (
              <button
                onClick={() => setDiscrepancyModalOpen(true)}
                className="flex items-center gap-2 bg-pt-red/20 text-pt-red px-4 py-2 rounded border border-pt-red/30 hover:bg-pt-red/30"
              >
                <AlertTriangle className="w-4 h-4" />
                Flag Discrepancy
              </button>
            )}

            {logEntry.discrepancy_flag && !logEntry.discrepancy_resolved && (
              <button
                onClick={() => setResolutionModalOpen(true)}
                className="flex items-center gap-2 bg-pt-green/20 text-pt-green px-4 py-2 rounded border border-pt-green/30 hover:bg-pt-green/30"
              >
                <CheckCircle className="w-4 h-4" />
                Resolve Discrepancy
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status Banners */}
      {logEntry.discrepancy_flag && (
        <div
          className={`mb-6 p-4 rounded border ${
            logEntry.discrepancy_resolved
              ? 'bg-pt-green/10 border-pt-green/30'
              : 'bg-pt-red/10 border-pt-red/30'
          }`}
        >
          <div className="flex items-center gap-3">
            {logEntry.discrepancy_resolved ? (
              <CheckCircle className="w-6 h-6 text-pt-green" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-pt-red" />
            )}
            <div className="flex-1">
              <div className="font-bold">
                {logEntry.discrepancy_resolved ? 'Discrepancy Resolved' : 'Discrepancy Flagged'}
              </div>
              <div className="text-sm text-pt-text-muted">
                Type: {logEntry.discrepancy_type} · Amount: {logEntry.discrepancy_amount}{' '}
                {logEntry.unit_of_measure}
              </div>
              {logEntry.discrepancy_notes && (
                <div className="text-sm mt-1">{logEntry.discrepancy_notes}</div>
              )}
              {logEntry.discrepancy_resolved_at && (
                <div className="text-xs text-pt-text0 mt-1">
                  Resolved: {format(new Date(logEntry.discrepancy_resolved_at), 'MMM dd, yyyy h:mm a')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {logEntry.transaction_type === 'waste' && !logEntry.witness_verified && (
        <div className="mb-6 p-4 rounded border bg-pt-amber/10 border-pt-amber/30">
          <div className="flex items-center gap-3">
            <UserCheck className="w-6 h-6 text-pt-amber" />
            <div>
              <div className="font-bold">Witness Verification Required</div>
              <div className="text-sm text-pt-text-muted">
                This waste transaction requires verification by a second licensed provider
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Transaction Info */}
        <div className="space-y-6">
          {/* Drug Information */}
          <div className="bg-navy-900/50 border border-pt-cyan/20 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-pt-cyan" />
              Drug Information
            </h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-pt-text-muted">Drug Name</label>
                <div className="text-lg font-bold">{logEntry.drug_name}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-pt-text-muted">Strength</label>
                  <div>{logEntry.drug_strength}</div>
                </div>
                <div>
                  <label className="text-sm text-pt-text-muted">Form</label>
                  <div>{logEntry.drug_form}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-pt-text-muted">DEA Schedule</label>
                  <div className="text-lg font-bold text-pt-red">Schedule {logEntry.dea_schedule}</div>
                </div>
                <div>
                  <label className="text-sm text-pt-text-muted">NDC Code</label>
                  <div className="font-mono">{logEntry.ndc_code || '—'}</div>
                </div>
              </div>
              {logEntry.lot_number && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-pt-text-muted">Lot Number</label>
                    <div className="font-mono">{logEntry.lot_number}</div>
                  </div>
                  <div>
                    <label className="text-sm text-pt-text-muted">Expiration</label>
                    <div>
                      {logEntry.expiration_date
                        ? format(new Date(logEntry.expiration_date), 'MMM dd, yyyy')
                        : '—'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Transaction Details */}
          <div className="bg-navy-900/50 border border-pt-cyan/20 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Transaction Details</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-pt-text-muted">Transaction Type</label>
                <div>
                  <span className="bg-pt-cyan/20 text-pt-cyan px-3 py-1 rounded text-sm">
                    {getTransactionTypeLabel(logEntry.transaction_type)}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-pt-text-muted">Quantity</label>
                  <div className="text-lg font-mono">
                    {logEntry.quantity_received ||
                      logEntry.quantity_administered ||
                      logEntry.quantity_wasted ||
                      0}{' '}
                    {logEntry.unit_of_measure}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-pt-text-muted">Remaining</label>
                  <div className="text-lg font-mono">
                    {logEntry.quantity_remaining} {logEntry.unit_of_measure}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-pt-text-muted">Running Balance</label>
                  <div className="text-lg font-mono font-bold text-pt-cyan">
                    {logEntry.running_balance} {logEntry.unit_of_measure}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Supply Chain (for receipts) */}
          {logEntry.transaction_type === 'receipt' && (
            <div className="bg-navy-900/50 border border-pt-cyan/20 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Supply Chain Information</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-pt-text-muted">Supplier</label>
                  <div>{logEntry.supplier_name || '—'}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-pt-text-muted">Invoice Number</label>
                    <div className="font-mono">{logEntry.invoice_number || '—'}</div>
                  </div>
                  <div>
                    <label className="text-sm text-pt-text-muted">DEA Form 222</label>
                    <div className="font-mono">{logEntry.form_222_number || '—'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - People & Notes */}
        <div className="space-y-6">
          {/* Provider Information */}
          <div className="bg-navy-900/50 border border-pt-cyan/20 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Provider Information</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-pt-text-muted">Provider</label>
                <div className="font-medium">{logEntry.provider_name}</div>
              </div>
              {logEntry.provider_dea_number && (
                <div>
                  <label className="text-sm text-pt-text-muted">DEA Number</label>
                  <div className="font-mono">{logEntry.provider_dea_number}</div>
                </div>
              )}
            </div>
          </div>

          {/* Patient Information (for administration) */}
          {logEntry.patient_name && (
            <div className="bg-navy-900/50 border border-pt-cyan/20 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Patient Information</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-pt-text-muted">Patient</label>
                  <div className="font-medium">{logEntry.patient_name}</div>
                </div>
                {logEntry.procedure_name && (
                  <div>
                    <label className="text-sm text-pt-text-muted">Procedure</label>
                    <div>{logEntry.procedure_name}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Witness Information */}
          {logEntry.transaction_type === 'waste' && (
            <div className="bg-navy-900/50 border border-pt-cyan/20 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-pt-amber" />
                Witness Verification
              </h2>
              {logEntry.witness_verified ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-pt-green">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Verified</span>
                  </div>
                  <div>
                    <label className="text-sm text-pt-text-muted">Witness</label>
                    <div className="font-medium">{logEntry.witness_name}</div>
                  </div>
                  <div>
                    <label className="text-sm text-pt-text-muted">Verification Time</label>
                    <div>
                      {logEntry.witness_signature_timestamp
                        ? format(new Date(logEntry.witness_signature_timestamp), 'MMM dd, yyyy h:mm a')
                        : '—'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-pt-amber">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span>Pending Verification</span>
                  </div>
                  <p className="text-sm text-pt-text-muted">
                    A second licensed provider must verify this waste transaction
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="bg-navy-900/50 border border-pt-cyan/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Notes</h2>
              {!editMode ? (
                <button
                  onClick={() => {
                    setEditMode(true);
                    setEditNotes(logEntry.notes || '');
                  }}
                  className="text-pt-cyan hover:text-pt-cyan"
                >
                  <Edit className="w-4 h-4" />
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => updateNotesMutation.mutate(editNotes)}
                    className="text-pt-green hover:text-pt-green"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditMode(false)}
                    className="text-pt-red hover:text-pt-red"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            {editMode ? (
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={6}
                className="w-full bg-navy-800 border border-pt-cyan/30 rounded p-3 text-pt-text"
              />
            ) : (
              <p className="text-pt-text whitespace-pre-wrap">{logEntry.notes || 'No notes'}</p>
            )}
          </div>

          {/* Audit Trail */}
          <div className="bg-navy-900/50 border border-pt-cyan/20 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4">Audit Trail</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-pt-text-muted">Created By:</span>
                <span>{logEntry.created_by_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-pt-text-muted">Created At:</span>
                <span>{format(new Date(logEntry.created_at), 'MMM dd, yyyy h:mm a')}</span>
              </div>
              {logEntry.modified_at && (
                <div className="flex justify-between">
                  <span className="text-pt-text-muted">Last Modified:</span>
                  <span>{format(new Date(logEntry.modified_at), 'MMM dd, yyyy h:mm a')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Witness Modal */}
      {witnessModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-navy-900 border border-pt-cyan/30 rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Verify Witness</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-pt-text-muted mb-2">Select Witness Provider</label>
                <select
                  value={witnessId}
                  onChange={(e) => {
                    setWitnessId(e.target.value);
                    const provider = providers?.find((p) => p.provider_id === e.target.value);
                    if (provider) {
                      setWitnessName(`${provider.first_name} ${provider.last_name}`);
                    }
                  }}
                  className="w-full bg-navy-800 border border-pt-cyan/30 rounded px-3 py-2 text-pt-text"
                >
                  <option value="">Select provider...</option>
                  {providers?.map((provider) => (
                    <option key={provider.provider_id} value={provider.provider_id}>
                      {provider.first_name} {provider.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => verifyWitnessMutation.mutate()}
                  disabled={!witnessId || verifyWitnessMutation.isPending}
                  className="flex-1 bg-pt-green/20 text-pt-green px-4 py-2 rounded border border-pt-green/30 hover:bg-pt-green/30 disabled:opacity-50"
                >
                  {verifyWitnessMutation.isPending ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  onClick={() => setWitnessModalOpen(false)}
                  className="flex-1 bg-pt-text-dim/20 text-pt-text-muted px-4 py-2 rounded border border-pt-input-border hover:bg-pt-text-dim/30"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discrepancy Modal */}
      {discrepancyModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-navy-900 border border-pt-cyan/30 rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Flag Discrepancy</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-pt-text-muted mb-2">Discrepancy Type</label>
                <select
                  value={discrepancyType}
                  onChange={(e) => setDiscrepancyType(e.target.value)}
                  className="w-full bg-navy-800 border border-pt-cyan/30 rounded px-3 py-2 text-pt-text"
                >
                  <option value="">Select type...</option>
                  <option value="count_variance">Count Variance</option>
                  <option value="documentation_error">Documentation Error</option>
                  <option value="theft">Theft</option>
                  <option value="loss">Loss</option>
                  <option value="spillage">Spillage</option>
                  <option value="expired_destroyed">Expired/Destroyed</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-pt-text-muted mb-2">
                  Discrepancy Amount ({logEntry.unit_of_measure})
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={discrepancyAmount}
                  onChange={(e) => setDiscrepancyAmount(e.target.value)}
                  className="w-full bg-navy-800 border border-pt-cyan/30 rounded px-3 py-2 text-pt-text"
                />
              </div>
              <div>
                <label className="block text-sm text-pt-text-muted mb-2">Notes</label>
                <textarea
                  value={discrepancyNotes}
                  onChange={(e) => setDiscrepancyNotes(e.target.value)}
                  rows={4}
                  className="w-full bg-navy-800 border border-pt-cyan/30 rounded p-3 text-pt-text"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => flagDiscrepancyMutation.mutate()}
                  disabled={
                    !discrepancyType || !discrepancyAmount || flagDiscrepancyMutation.isPending
                  }
                  className="flex-1 bg-pt-red/20 text-pt-red px-4 py-2 rounded border border-pt-red/30 hover:bg-pt-red/30 disabled:opacity-50"
                >
                  {flagDiscrepancyMutation.isPending ? 'Flagging...' : 'Flag Discrepancy'}
                </button>
                <button
                  onClick={() => setDiscrepancyModalOpen(false)}
                  className="flex-1 bg-pt-text-dim/20 text-pt-text-muted px-4 py-2 rounded border border-pt-input-border hover:bg-pt-text-dim/30"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resolution Modal */}
      {resolutionModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-navy-900 border border-pt-cyan/30 rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Resolve Discrepancy</h2>
            <div className="space-y-4">
              <div className="bg-pt-red/10 border border-pt-red/30 rounded p-4">
                <div className="text-sm text-pt-text-muted mb-1">Original Discrepancy:</div>
                <div className="font-medium">
                  {logEntry.discrepancy_type} - {logEntry.discrepancy_amount}{' '}
                  {logEntry.unit_of_measure}
                </div>
                {logEntry.discrepancy_notes && (
                  <div className="text-sm mt-2">{logEntry.discrepancy_notes}</div>
                )}
              </div>
              <div>
                <label className="block text-sm text-pt-text-muted mb-2">Resolution Notes</label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  rows={6}
                  placeholder="Describe how this discrepancy was resolved..."
                  className="w-full bg-navy-800 border border-pt-cyan/30 rounded p-3 text-pt-text"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => resolveDiscrepancyMutation.mutate()}
                  disabled={!resolutionNotes || resolveDiscrepancyMutation.isPending}
                  className="flex-1 bg-pt-green/20 text-pt-green px-4 py-2 rounded border border-pt-green/30 hover:bg-pt-green/30 disabled:opacity-50"
                >
                  {resolveDiscrepancyMutation.isPending ? 'Resolving...' : 'Resolve'}
                </button>
                <button
                  onClick={() => setResolutionModalOpen(false)}
                  className="flex-1 bg-pt-text-dim/20 text-pt-text-muted px-4 py-2 rounded border border-pt-input-border hover:bg-pt-text-dim/30"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
